
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";

interface HeroSectionProps {
  onPrimaryClick?: () => void;
}

const HeroSection = ({ onPrimaryClick }: HeroSectionProps) => {
  const { user } = useAuth();
  const [cyclingWord, setCyclingWord] = useState("people");
  const cyclingWords = ["people", "followers", "teammates","managers","AI agents","customers","investors"];

  useEffect(() => {
    const interval = setInterval(() => {
      setCyclingWord(prev => {
        const currentIndex = cyclingWords.indexOf(prev);
        const nextIndex = (currentIndex + 1) % cyclingWords.length;
        return cyclingWords[nextIndex];
      });
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  const { data: userProfile } = useQuery({
    queryKey: ["user-profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("users")
        .select("subscription_type")
        .eq('id', user.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user
  });

  const getButtonConfig = () => {
    if (!user) {
      return {
        primary: { text: "Create Posit", link: "/signup" },
        secondary: { text: "Explore Posits", link: "/public-posits" }
      };
    }
    
    const isFreeUser = userProfile?.subscription_type === 'free';
    if (isFreeUser) {
      return {
        primary: { text: "Create Posit", link: "/create-posit" },
        secondary: { text: "Explore Posits", link: "/public-posits" }
      };
    }
    
    return {
      primary: { text: "Create Posit", link: "/create-posit" },
      secondary: { text: "Explore Posits", link: "/public-posits" }
    };
  };

  const buttonConfig = getButtonConfig();

  return (
    <section className="container mx-auto px-4 pt-12 pb-4 md:pt-16 md:pb-8 relative">
      <div className="text-center max-w-4xl mx-auto space-y-6 md:space-y-8">
        <h1 
          className="text-4xl sm:text-5xl md:text-7xl font-bold text-foreground opacity-0 animate-[fadeIn_0.8s_ease-out_0.2s_forwards] leading-tight"
        >
          <div>Another you when</div>
          <div>
            <span className="text-primary inline-block transition-all duration-6000 ease-in-out">
              {cyclingWord}
            </span>{" "}
            need you
          </div>
        </h1>
        <p 
          className="text-lg sm:text-xl md:text-2xl text-foreground/90 leading-relaxed opacity-0 animate-[fadeIn_0.8s_ease-out_0.6s_forwards] font-medium max-w-2xl mx-auto"
        >
          AI with your knowledge and experience
        </p>

        <div 
          className="flex flex-row gap-3 sm:gap-4 justify-center opacity-0 animate-[fadeIn_0.8s_ease-out_1.4s_forwards] pt-2"
        >
          <Button asChild size="lg" className="text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 font-semibold bg-primary text-white hover:bg-primary/90 shadow-sm hover:shadow flex-1 sm:flex-none">
            <Link to={buttonConfig.primary.link} onClick={onPrimaryClick}>
              {buttonConfig.primary.text}
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 font-semibold border-2 hover:bg-primary/10 flex-1 sm:flex-none">
            <Link to={buttonConfig.secondary.link}>
              {buttonConfig.secondary.text}
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;

